"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { SelectIcon } from "../../icons/index.js";
import makeStyles from "../../theming/make-styles.js";
import DropdownBase from "../dropdown-base/dropdown-base.js";
import Input from "../input/index.js";
import List, { ListGroup, ListItem } from "../list/index.js";
import joinArray from "../utils/join-array.js";
import useCombinedRefs from "../utils/use-combined-refs.js";
import useKeyboardNavigation from "../utils/use-keyboard-navigation.js";
const useStyles = makeStyles()((theme, { disabled }) => ({
    group: {
        "&:not(:last-child)": {
            marginBottom: theme.spacing("md"),
        },
    },
    input: {
        cursor: disabled ? "not-allowed" : "pointer",
    },
}));
function processOptions(children) {
    const grouped = React.Children.map(children, (child) => "value" in child.props ? child.props : Object.assign(Object.assign({}, child.props), { elements: React.Children.map(child.props.children, child => child.props) }));
    const flat = grouped.flatMap(opt => ("value" in opt ? opt : opt.elements));
    const flatWithoutSelectAll = flat.filter(opt => !opt.selectAll);
    const byId = Object.fromEntries(flat.map(opt => [opt.value, opt]));
    return { byId, flat, flatWithoutSelectAll, grouped };
}
function defaultRenderValue(value, options) {
    var _a;
    return Array.isArray(value)
        ? joinArray(value.map(v => { var _a; return (_a = options[v]) === null || _a === void 0 ? void 0 : _a.text; }), 10)
        : value
            ? (_a = options[value]) === null || _a === void 0 ? void 0 : _a.text
            : "";
}
export const Select = React.forwardRef(function Select(_a, ref) {
    var { block, children, className, disabled, leftIcon, multiple, size = "medium", strategy, valid, value, variant, onChange, onClose, onClick, onOpen, renderValue = defaultRenderValue } = _a, props = __rest(_a, ["block", "children", "className", "disabled", "leftIcon", "multiple", "size", "strategy", "valid", "value", "variant", "onChange", "onClose", "onClick", "onOpen", "renderValue"]);
    const [preselectionText, setPreselectionText] = useState("");
    const attachmentRef = useRef(null);
    const { classes, cx } = useStyles({ disabled });
    const innerRef = useRef(null);
    const combinedRef = useCombinedRefs(ref, innerRef);
    const openRef = useRef(null);
    const options = useMemo(() => processOptions(children), [children]);
    const labels = useMemo(() => options.flat.filter(opt => !opt.disabled).map(opt => opt.value), [options]);
    const { current: preselection, handleKeyDown, setPreselection, } = useKeyboardNavigation(labels, (action, current) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        switch (action) {
            case "close": {
                (_a = openRef.current) === null || _a === void 0 ? void 0 : _a.setOpen(false);
                break;
            }
            case "toggle": {
                if (!((_b = openRef.current) === null || _b === void 0 ? void 0 : _b.open)) {
                    (_c = openRef.current) === null || _c === void 0 ? void 0 : _c.setOpen(true);
                    onOpen === null || onOpen === void 0 ? void 0 : onOpen();
                }
                else if (current) {
                    handleAct(current);
                }
                break;
            }
            case "type": {
                const available = options.flat.filter(opt => !opt.disabled);
                const letter = current;
                const first = available.findIndex(opt => typeof opt.text === "string" && opt.text.toUpperCase().startsWith(preselectionText + letter));
                if (!letter) {
                    return;
                }
                const onSingleChange = !multiple ? onChange : undefined;
                if (first !== -1) {
                    setPreselectionText(preselectionText + letter);
                    (_e = (((_d = openRef.current) === null || _d === void 0 ? void 0 : _d.open) ? setPreselection : onSingleChange)) === null || _e === void 0 ? void 0 : _e(available[first].value);
                    return;
                }
                if (letter === preselectionText[preselectionText.length - 1]) {
                    const preselectedIndex = preselection ? available.findIndex(opt => opt.value === preselection) : available.findIndex(opt => opt.value === value);
                    const second = available.findIndex((opt, i) => i > preselectedIndex && opt.text.toUpperCase().startsWith(preselectionText));
                    if (second !== -1) {
                        setPreselectionText(preselectionText + "");
                        (_g = (((_f = openRef.current) === null || _f === void 0 ? void 0 : _f.open) ? setPreselection : onSingleChange)) === null || _g === void 0 ? void 0 : _g(available[second].value);
                        return;
                    }
                }
                const third = available.findIndex(opt => typeof opt.text === "string" && opt.text.toUpperCase().startsWith(letter));
                if (third !== -1) {
                    setPreselectionText(letter);
                    (_j = (((_h = openRef.current) === null || _h === void 0 ? void 0 : _h.open) ? setPreselection : onSingleChange)) === null || _j === void 0 ? void 0 : _j(available[third].value);
                    return;
                }
                break;
            }
        }
    });
    const handleAct = useCallback((newValue) => {
        var _a, _b;
        if (multiple) {
            if (options.byId[newValue].selectAll) {
                const available = options.flatWithoutSelectAll.filter(opt => !opt.disabled);
                onChange === null || onChange === void 0 ? void 0 : onChange(available.length === (value === null || value === void 0 ? void 0 : value.length) ? [] : available.map(opt => opt.value));
            }
            else if (value === null || value === void 0 ? void 0 : value.includes(newValue)) {
                onChange === null || onChange === void 0 ? void 0 : onChange(value.filter(v => v !== newValue));
            }
            else {
                onChange === null || onChange === void 0 ? void 0 : onChange([...(value || []), newValue]);
            }
        }
        else {
            onChange === null || onChange === void 0 ? void 0 : onChange(newValue);
        }
        (_a = innerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        if (!multiple) {
            (_b = openRef.current) === null || _b === void 0 ? void 0 : _b.setOpen(false);
        }
    }, [multiple, onChange, options, value]);
    const handleBlur = useCallback((e) => {
        var _a;
        const attachment = attachmentRef.current;
        const focusedElement = e.relatedTarget;
        if (focusedElement) {
            if (focusedElement === innerRef.current || ((_a = innerRef.current) === null || _a === void 0 ? void 0 : _a.contains(focusedElement))) {
                return;
            }
            if (focusedElement === attachment || (attachment === null || attachment === void 0 ? void 0 : attachment.contains(focusedElement))) {
                return;
            }
        }
    }, []);
    const handleClick = useCallback((e) => {
        var _a;
        if (!((_a = openRef.current) === null || _a === void 0 ? void 0 : _a.open)) {
            onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    }, [onClick, onOpen]);
    const triggerElement = (_jsx(Input, Object.assign({ block: block, className: cx(classes.input, className), disabled: disabled, leftIcon: leftIcon, readOnly: true, rightIcon: props => { var _a; return _jsx(SelectIcon, Object.assign({ open: ((_a = openRef.current) === null || _a === void 0 ? void 0 : _a.open) || false }, props)); }, size: size, valid: valid, value: renderValue(value, options.byId), variant: variant }, props, { onBlur: handleBlur, onKeyDown: handleKeyDown })));
    return (_jsx(DropdownBase, { ref: combinedRef, block: block, "data-component": "select", openRef: openRef, strategy: strategy, triggerElement: triggerElement, triggerStrategy: "toggle", onClick: handleClick, onClose: onClose, children: _jsx(List, { hoverable: true, role: "listbox", children: options.grouped.map((opt, i) => "value" in opt ? (_jsx(ListItem, Object.assign({ active: multiple ? Array.isArray(value) && value.includes(opt.value) : opt.value === value, "aria-selected": multiple ? Array.isArray(value) && value.includes(opt.value) : opt.value === value, "data-item-id": opt.value, role: "option" }, opt, { hoverable: opt.value === preselection, scrollToView: opt.value === value || opt.value === preselection, onClick: () => !opt.disabled && handleAct(opt.value), onMouseEnter: () => setPreselection(opt.value), onMouseLeave: () => setPreselection(), children: opt.children || opt.text }), opt.value)) : (_jsx(ListGroup, { className: classes.group, label: opt.label, children: opt.elements.map(opt => (_jsx(ListItem, Object.assign({ active: multiple ? Array.isArray(value) && value.includes(opt.value) : opt.value === value, "aria-selected": multiple ? Array.isArray(value) && value.includes(opt.value) : opt.value === value, "data-item-id": opt.value, role: "option" }, opt, { hoverable: opt.value === preselection, scrollToView: opt.value === value || opt.value === preselection, onClick: () => !opt.disabled && handleAct(opt.value), onMouseEnter: () => setPreselection(opt.value), onMouseLeave: () => setPreselection(), children: opt.children || opt.text }), opt.value))) }, i))) }) }));
});
export const Option = (_props) => null;
export const OptionGroup = (_props) => null;
export default Select;
//# sourceMappingURL=select.js.map